import React, { useState } from 'react';
import { Artifact, ArtifactType } from '../../types/artifact';
import { ArtifactTileBody, TileViewMode } from './ArtifactTileBody';
import { useTheme } from '../../contexts/ThemeContext';
import 'highlight.js/styles/github-dark.css';

interface ArtifactViewerProps {
  artifact: Artifact;
}

export const ArtifactViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const [viewMode, setViewMode] = useState<string>('default');
  const { darkMode } = useTheme();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  
  // Function to copy artifact content to clipboard
  const copyArtifactContent = () => {
    let contentToCopy = '';
    
    if (typeof artifact.content === 'string') {
      contentToCopy = artifact.content;
    } else if (artifact.content && typeof artifact.content === 'object') {
      try {
        contentToCopy = JSON.stringify(artifact.content, null, 2);
      } catch (e) {
        contentToCopy = 'Error: Could not stringify content';
      }
    } else {
      contentToCopy = 'No content available';
    }
    
    navigator.clipboard.writeText(contentToCopy);
    setCopiedToClipboard(true);
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopiedToClipboard(false);
    }, 2000);
  };
  
  const getAvailableViewModes = () => {
    const modes: Record<string, string> = {
      default: 'Default'
    };
    
    switch (artifact.type) {
      case ArtifactType.CODE:
        modes.editor = 'Editor';
        modes.preview = 'Preview';
        break;
      case ArtifactType.DOCUMENT:
        modes.formatted = 'Formatted';
        modes.raw = 'Raw';
        break;
      case ArtifactType.DIAGRAM:
        modes.interactive = 'Interactive';
        modes.static = 'Static';
        break;
    }
    
    return modes;
  };
  
  const viewModes = getAvailableViewModes();
  const hasMultipleViewModes = Object.keys(viewModes).length > 1;
  
  return (
    <div className={`artifact-viewer rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div>
          <h3 className="font-medium text-lg">{artifact.name}</h3>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {artifact.type} • Created by {artifact.agent_name || artifact.created_by || artifact.persona_id} • Version {artifact.version || '1'}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            className={`px-3 py-1 rounded text-sm flex items-center ${
              darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
            onClick={copyArtifactContent}
          >
            {copiedToClipboard ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy Content
              </>
            )}
          </button>
        
          {hasMultipleViewModes && (
            <div className="flex space-x-1">
              {Object.entries(viewModes).map(([mode, label]) => (
                <button
                  key={mode}
                  className={`px-3 py-1 text-sm rounded ${
                    viewMode === mode
                      ? 'bg-blue-600 text-white'
                      : darkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => setViewMode(mode)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="p-4">
        <ArtifactTileBody
          artifact={artifact}
          viewMode={viewMode as TileViewMode}
        />
      </div>
      
      {artifact.metadata && Object.keys(artifact.metadata).length > 0 && (
        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : ''}`}>
          <h4 className="font-medium mb-2">Metadata</h4>
          <div className={`p-3 rounded text-sm ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <pre className={`whitespace-pre-wrap ${darkMode ? 'text-gray-300' : ''}`}>{JSON.stringify(artifact.metadata, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
